import { type MainDataType } from '@constants/types'
import useApi from '@hooks/useApi'
import Logger from '@logger'
import { type Metric } from '@shared_types/vessel_data'
import { isIntInRange } from '@shared_utils/number_utils'
import moment from 'moment-timezone'
import { useEffect, useRef } from 'react'

import useSocket from './useSocket'

const logger = Logger.create('useRealTimeClock')

export default function useRealTimeClock () {
  const { apiGet } = useApi()
  const rtc = useRef(moment())
  const rtcTs = useRef(-1)
  const synced = useRef(false)
  const syncing = useRef(false)

  const Y = useRef(0)
  const M = useRef(0)
  const D = useRef(0)

  const h = useRef(0)
  const m = useRef(0)
  const s = useRef(0)

  const updateRtc = () => {
    if (!isIntInRange(Y.current, 2019, undefined)) {
      logger.debug(`year not OK ${Y.current}`)
    } else if (!isIntInRange(M.current, 1, 12)) {
      logger.debug(`month not OK ${M.current}`)
    } else if (!isIntInRange(D.current, 1, 31)) {
      logger.debug(`day not OK ${D.current}`)
    } else if (!isIntInRange(h.current, 0, 23)) {
      logger.debug(`hour not OK ${h.current}`)
    } else if (!isIntInRange(m.current, 0, 60)) {
      logger.debug(`minute not OK ${m.current}`)
    } else if (!isIntInRange(s.current, 0, 60)) {
      logger.debug(`seconds not OK ${s.current}`)
    } else {
      rtc.current.set({
        year: Y.current,
        month: M.current - 1,
        date: D.current,
        hours: h.current,
        minutes: m.current,
        seconds: s.current
      })

      rtcTs.current = rtc.current.toDate().getTime()
    }
  }

  useEffect(() => {
    if (!synced.current && !syncing.current) {
      syncing.current = true

      apiGet<MainDataType>('api/main')
        .then(res => {
          Y.current = res.localTimeYear.value
          M.current = res.localTimeMonth.value
          D.current = res.localTimeDay.value
          h.current = res.localTimeHour.value
          m.current = res.localTimeMin.value
          s.current = res.localTimeSec.value
          synced.current = true
          syncing.current = false
          updateRtc()
        })
        .catch(() => {
          synced.current = true
          syncing.current = false
        })
    }
  }, [synced.current, syncing.current])

  useSocket<Metric>('main.localTimeHour', evt => {
    logger.debug(`localTimeHour = ${evt.value}`)
    h.current = evt.value
    updateRtc()
  })

  useSocket<Metric>('main.localTimeMin', evt => {
    logger.debug(`localTimeMin = ${evt.value}`)
    m.current = evt.value
    updateRtc()
  })

  useSocket<Metric>('main.localTimeSec', evt => {
    logger.debug(`localTimeSec = ${evt.value}`)
    s.current = evt.value
    updateRtc()
  })

  useSocket<Metric>('main.localTimeYear', evt => {
    logger.debug(`localTimeYear = ${evt.value}`)
    Y.current = evt.value
    updateRtc()
  })

  useSocket<Metric>('main.localTimeMonth', evt => {
    logger.debug(`localTimeMonth = ${evt.value}`)
    M.current = evt.value
    updateRtc()
  })

  useSocket<Metric>('main.localTimeDay', evt => {
    logger.debug(`localTimeDay = ${evt.value}`)
    D.current = evt.value
    updateRtc()
  })

  return { synced, rtcTs, rtc }
}
